const MODERATION_RULES = [
  {
    id: 'contact_off_platform',
    severity: 'high',
    action: 'quarantine',
    label: 'Possible attempt to move a child conversation off-platform',
    patterns: [
      /\b(?:snapchat|telegram|whatsapp|signal|discord|kik)\b/i,
      /\b(?:dm me privately|private chat|secret chat)\b/i,
      /\b(?:meet me alone|come alone)\b/i,
    ],
  },
  {
    id: 'sexual_content',
    severity: 'critical',
    action: 'quarantine',
    label: 'Sexual or grooming-related language',
    patterns: [
      /\b(?:nude|nudes|sexy pics?|explicit|sexual)\b/i,
      /\b(?:send (?:me )?(?:a )?photo|send pics?)\b/i,
      /\b(?:kiss me|touch me)\b/i,
    ],
  },
  {
    id: 'harassment',
    severity: 'critical',
    action: 'quarantine',
    label: 'Harassment, abuse, or threatening language',
    patterns: [
      /\b(?:kill yourself|i'?ll hurt you|you should die)\b/i,
      /\b(?:slut|bitch|retard|pedo)\b/i,
      /\b(?:hate you|stupid idiot)\b/i,
    ],
  },
  {
    id: 'personal_contact',
    severity: 'high',
    action: 'quarantine',
    label: 'Personal contact details shared in a public discussion',
    patterns: [
      /\b\d{5,}\b/,
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i,
    ],
  },
];

const ADULT_LINK_REASON = 'Adult or explicit link destination';

const ADULT_HOST_LABELS = new Set([
  'adult',
  'camgirl',
  'camgirls',
  'escort',
  'escorts',
  'hentai',
  'nude',
  'nudes',
  'porn',
  'porno',
  'sex',
  'sexy',
  'xxx',
]);

const ADULT_HOST_KEYWORDS = [
  'brazzers',
  'chaturbate',
  'erome',
  'fansly',
  'fetlife',
  'livejasmin',
  'manyvids',
  'onlyfans',
  'pornhub',
  'redtube',
  'spankbang',
  'stripchat',
  'xhamster',
  'xnxx',
  'xvideos',
  'youporn',
];

const ADULT_URL_PATH_PATTERN = /(?:^|[^\w])(?:adult|camgirls?|escort(?:s)?|explicit[-_]?sex|hentai|nudes?|porn|porno|xxx)(?:[^\w]|$)/i;

function decodeHtmlAttribute(value = '') {
  return String(value || '')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

function trimUrlCandidate(value = '') {
  return decodeHtmlAttribute(value)
    .trim()
    .replace(/^[<([{'"`]+/, '')
    .replace(/[>)\]}'"`.,!?;:]+$/, '');
}

function extractBoardUrls(input = '') {
  const source = String(input || '');
  const urls = [];
  const hrefPattern = /\s(?:href|src)=["']([^"']+)["']/gi;
  const urlPattern = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

  let match = hrefPattern.exec(source);
  while (match) {
    const candidate = trimUrlCandidate(match[1]);
    if (candidate) urls.push(candidate);
    match = hrefPattern.exec(source);
  }

  match = urlPattern.exec(source);
  while (match) {
    const candidate = trimUrlCandidate(match[0]);
    if (candidate) urls.push(candidate);
    match = urlPattern.exec(source);
  }

  return Array.from(new Set(urls));
}

function parseUrlCandidate(value = '') {
  const candidate = trimUrlCandidate(value);
  if (!candidate || /^(?:mailto|tel):/i.test(candidate)) return null;

  const normalized = /^[a-z][a-z\d+.-]*:/i.test(candidate)
    ? candidate
    : `https://${candidate.replace(/^\/\//, '')}`;

  try {
    return new URL(normalized);
  } catch {
    return null;
  }
}

function isAdultLinkUrl(value = '') {
  const parsed = parseUrlCandidate(value);
  if (!parsed) return false;

  const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
  const labels = host.split('.').filter(Boolean);
  const compactHost = labels.join('');
  const topLevelDomain = labels[labels.length - 1] || '';

  if (topLevelDomain === 'xxx') return true;
  if (labels.some((label) => ADULT_HOST_LABELS.has(label))) return true;
  if (ADULT_HOST_KEYWORDS.some((keyword) => compactHost.includes(keyword))) return true;

  return ADULT_URL_PATH_PATTERN.test(`${parsed.pathname}${parsed.search}${parsed.hash}`);
}

function escapeHtml(value = '') {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getHtmlAttribute(attrs = '', name = '') {
  const pattern = new RegExp(`\\s${name}=["']([^"']*)["']`, 'i');
  const match = pattern.exec(String(attrs || ''));
  return match ? decodeHtmlAttribute(match[1]) : '';
}

function isAllowedGiphyUrl(value = '') {
  try {
    const parsed = new URL(String(value || ''));
    return parsed.protocol === 'https:' && /(^|\.)giphy\.com$/i.test(parsed.hostname);
  } catch {
    return false;
  }
}

function sanitizeGiphyImageTag(attrs = '') {
  const id = getHtmlAttribute(attrs, 'data-giphy-id') || getHtmlAttribute(attrs, 'data-id');
  const src = getHtmlAttribute(attrs, 'src');
  if (!id || !isAllowedGiphyUrl(src)) return '';
  const title = getHtmlAttribute(attrs, 'alt') || getHtmlAttribute(attrs, 'data-giphy-title') || 'GIF';
  const rating = getHtmlAttribute(attrs, 'data-giphy-rating') || 'g';
  const width = Number(getHtmlAttribute(attrs, 'width') || 0) || 0;
  const height = Number(getHtmlAttribute(attrs, 'height') || 0) || 0;
  return `<figure data-giphy-embed="true" data-giphy-id="${escapeHtml(id)}" data-giphy-title="${escapeHtml(title)}" data-giphy-rating="${escapeHtml(rating)}"><img data-giphy-image="true" data-giphy-id="${escapeHtml(id)}" src="${escapeHtml(src)}" alt="${escapeHtml(title)}"${width ? ` width="${width}"` : ''}${height ? ` height="${height}"` : ''}><figcaption class="giphy-attribution">Powered by GIPHY</figcaption></figure>`;
}

function sanitizeGiphyMarkup(input = '') {
  let safe = String(input || '');
  const figures = [];
  safe = safe.replace(/<figure\b[^>]*data-giphy-embed=["']true["'][\s\S]*?<\/figure>/gi, (figure) => {
    const imageMatch = /<img\b([^>]*)>/i.exec(figure);
    if (!imageMatch) return '';
    const figureAttrs = /^<figure\b([^>]*)>/i.exec(figure)?.[1] || '';
    const mergedAttrs = `${imageMatch[1]} data-giphy-id="${escapeHtml(getHtmlAttribute(figureAttrs, 'data-giphy-id'))}" data-giphy-title="${escapeHtml(getHtmlAttribute(figureAttrs, 'data-giphy-title'))}" data-giphy-rating="${escapeHtml(getHtmlAttribute(figureAttrs, 'data-giphy-rating'))}"`;
    const sanitized = sanitizeGiphyImageTag(mergedAttrs);
    if (!sanitized) return '';
    const token = `%%GIPHY_FIGURE_${figures.length}%%`;
    figures.push(sanitized);
    return token;
  });
  safe = safe.replace(/<img\b([^>]*)>/gi, (_match, attrs) => sanitizeGiphyImageTag(attrs));
  return safe.replace(/%%GIPHY_FIGURE_(\d+)%%/g, (_match, index) => figures[Number(index)] || '');
}

export function sanitizeBoardHtml(input = '') {
  return sanitizeGiphyMarkup(input)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son[a-z]+="[^"]*"/gi, '')
    .replace(/\son[a-z]+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

export function boardHtmlToText(input = '') {
  const stripped = String(input || '')
    .replace(/<figure\b[^>]*data-giphy-embed=["']true["'][\s\S]*?<\/figure>/gi, (figure) => {
      const title = getHtmlAttribute(/^<figure\b([^>]*)>/i.exec(figure)?.[1] || '', 'data-giphy-title');
      return title && title !== 'GIF' ? ` [GIF: ${title}] ` : ' [GIF] ';
    })
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
  return stripped.replace(/\s+/g, ' ').trim();
}

export function detectBoardModerationSignals(input = '') {
  const raw = String(input || '');
  const text = boardHtmlToText(raw);
  if (!text) return [];

  const reasons = [];
  MODERATION_RULES.forEach((rule) => {
    if (rule.patterns.some((pattern) => pattern.test(text))) {
      reasons.push(rule.label);
    }
  });
  if (extractBoardUrls(raw).some(isAdultLinkUrl)) {
    reasons.push(ADULT_LINK_REASON);
  }

  return Array.from(new Set(reasons));
}

export function getHiddenBoardContentPlaceholder() {
  return {
    bodyHtml: '<p><em>This content has been hidden by moderators.</em></p>',
    bodyText: 'This content has been hidden by moderators.',
  };
}
